import { config } from "../config";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import { waitForJobMetadata } from "./jobMetadataPolling";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const imageIntelligenceUrlPrefix = `${protocol}://${host}/clinics/v1/image-intelligence`;
const JOB_POLL_TIMEOUT_MS = 15 * 60 * 1000;

const getImageIntelligenceClinicUrlPrefix = (clinicId: string) => {
  if (!clinicId) {
    throw new Error("Clinic ID is required to process images.");
  }

  return `${imageIntelligenceUrlPrefix}/${clinicId}`;
};

const getCensorshipUrlPrefix = (clinicId: string) =>
  `${getImageIntelligenceClinicUrlPrefix(clinicId)}/censorship`;

export type TCensorshipUploadUrl = {
  s3_key: string;
  put_url: string;
};

export type TGenerateCensorshipUploadUrlsResponse = {
  batch_id: string;
  upload_urls: TCensorshipUploadUrl[];
};

export type TStartCensorshipJobResponse = {
  batch_id: string;
  download_urls: string[];
  metadata_url: string;
};

export type TImageFilterMode = "off" | "detect" | "apply";

export type TCensorshipImageFilter = {
  mode: TImageFilterMode;
  asset_url?: string;
};

export type TTattooImageFilter = {
  mode: TImageFilterMode;
  apply_params?: {
    blur_radius: number;
  };
};

export type TBackgroundImageFilter = {
  mode: TImageFilterMode;
  apply_params?: {
    alpha_threshold?: number;
  };
};

export type TFaceCensorshipApplyParams = {
  effect: "blur";
  mask_expansion_ratio: number;
  feather_ratio: number;
  blur_radius_ratio: number;
};

export type TFaceCensorshipImageFilter = {
  mode: TImageFilterMode;
  apply_params: TFaceCensorshipApplyParams;
};

export type TImageFiltersRequest = {
  censorship?: TCensorshipImageFilter;
  tattoo?: TTattooImageFilter;
  face_censorship?: TFaceCensorshipImageFilter;
  background?: TBackgroundImageFilter;
};

export type TFaceCensorshipPoint = {
  x: number;
  y: number;
};

export type TFaceCensorshipDetectedRegion = {
  id: string;
  label: "face" | string;
  shape_type: "polygon" | string;
  coord_space: "processed_image_px" | string;
  geometry: {
    points?: TFaceCensorshipPoint[];
  };
  helpers?: {
    bbox?: { x1: number; y1: number; x2: number; y2: number };
    source?: string;
    fallback_used?: boolean;
  };
};

export type TFaceCensorshipDetectionMetadata = {
  mode_used: TImageFilterMode;
  regions: TFaceCensorshipDetectedRegion[];
  warnings: string[];
};

export type TImageCoordinateFrameMetadata = {
  source_coord_space: "source_image_px";
  source_width_px: number;
  source_height_px: number;
  processed_coord_space: "processed_image_px";
  processed_width_px: number;
  processed_height_px: number;
  /** Exact counter-clockwise PIL quarter turn from source to processed. */
  source_to_processed_rotation_degrees_ccw: 0 | 90 | 180 | 270;
};

export type TCensorshipJobMetadata = {
  success: boolean;
  errors: Record<string, string> | null;
  images: Array<{
    input_s3_key: string;
    output_s3_key: string;
    coordinate_frame?: TImageCoordinateFrameMetadata | null;
    final_bboxes_on_original?: Record<
      string,
      [number, number, number, number] | null
    > | null;
    ai_filters?: {
      censorship?: {
        mode_used: TImageFilterMode;
        regions: unknown[];
      };
      face_censorship?: TFaceCensorshipDetectionMetadata;
      [filterName: string]: unknown;
    };
  }>;
};

export type TImageToolExportPlacement = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TImageToolOverlayExportOperation = {
  operation_type: "overlay";
  asset_url: string;
  placements: TImageToolExportPlacement[];
};

export type TImageToolTattooBlurExportOperation = {
  operation_type: "tattoo_blur";
  blur_radius: number;
};

export type TImageToolBackgroundRemoveExportOperation = {
  operation_type: "background_remove";
  alpha_threshold: number | null;
};

export type TImageToolFaceBlurExportRegion = {
  points: TFaceCensorshipPoint[];
};

export type TImageToolFaceBlurExportOperation = {
  operation_type: "face_blur";
  regions: TImageToolFaceBlurExportRegion[];
  mask_expansion_ratio: number;
  feather_ratio: number;
  blur_radius_ratio: number;
};

export type TImageToolExportOperation =
  | TImageToolTattooBlurExportOperation
  | TImageToolOverlayExportOperation
  | TImageToolFaceBlurExportOperation
  | TImageToolBackgroundRemoveExportOperation;

export type TStartImageToolExportResponse = {
  job_id: string;
  download_urls: string[];
  metadata_url: string;
};

export type TImageToolExportMetadata = {
  success: boolean;
  errors: Record<string, string> | null;
  images: Array<{
    input_s3_key: string;
    output_s3_key: string;
    output_thumbnail_s3_key: string;
    width: number;
    height: number;
    operations_applied: number;
  }>;
};

const getResponseError = async (response: Response, fallback: string) => {
  const body = await response
    .clone()
    .json()
    .catch(() => null);
  const detail = body?.detail || body?.message || body?.error;
  return typeof detail === "string"
    ? detail
    : `${fallback} (HTTP ${response.status}).`;
};

export const generateCensorshipUploadUrls = async (
  clinicId: string,
  numImages: number,
  signal?: AbortSignal,
): Promise<TGenerateCensorshipUploadUrlsResponse> => {
  const params = new URLSearchParams({ num_images: String(numImages) });
  const response = await fetchWithAuthRetry(
    `${getCensorshipUrlPrefix(clinicId)}/generate-upload-urls?${params.toString()}`,
    { method: "GET", signal },
  );

  if (!response.ok) {
    throw new Error(
      await getResponseError(response, "Unable to prepare the image upload"),
    );
  }

  return response.json();
};

export const uploadCensorshipImages = async ({
  files,
  uploadUrls,
  signal,
  onProgress,
}: {
  files: File[];
  uploadUrls: TCensorshipUploadUrl[];
  signal?: AbortSignal;
  onProgress?: (completed: number, total: number) => void;
}) => {
  if (files.length !== uploadUrls.length) {
    throw new Error("The upload could not be prepared. Please try again.");
  }

  let nextIndex = 0;
  let completed = 0;
  const workerCount = Math.min(4, files.length);

  const uploadNext = async (): Promise<void> => {
    const index = nextIndex;
    nextIndex += 1;
    if (index >= files.length) return;

    const response = await fetch(uploadUrls[index].put_url, {
      method: "PUT",
      headers: {
        "Content-Type": files[index].type || "image/png",
      },
      body: files[index],
      signal,
    });

    if (!response.ok) {
      throw new Error(
        `Image ${index + 1} could not be uploaded. Please retry the upload.`,
      );
    }

    completed += 1;
    onProgress?.(completed, files.length);
    await uploadNext();
  };

  await Promise.all(Array.from({ length: workerCount }, uploadNext));
};

export const startImageFiltersJob = async ({
  clinicId,
  batchId,
  numImages,
  aiFilters,
  signal,
}: {
  clinicId: string;
  batchId: string;
  numImages: number;
  aiFilters: TImageFiltersRequest;
  signal?: AbortSignal;
}): Promise<TStartCensorshipJobResponse> => {
  const response = await fetchWithAuthRetry(
    `${getImageIntelligenceClinicUrlPrefix(clinicId)}/image-filter-jobs`,
    {
      method: "POST",
      body: JSON.stringify({
        batch_id: batchId,
        num_images: numImages,
        ai_filters: aiFilters,
      }),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      await getResponseError(response, "Unable to start image processing"),
    );
  }

  return response.json();
};

export const startImageToolExport = async ({
  clinicId,
  images,
  signal,
}: {
  clinicId: string;
  images: Array<{
    input_s3_key: string;
    operations: TImageToolExportOperation[];
  }>;
  signal?: AbortSignal;
}): Promise<TStartImageToolExportResponse> => {
  if (!clinicId) {
    throw new Error("Clinic ID is required to export images.");
  }

  const response = await fetchWithAuthRetry(
    `${imageIntelligenceUrlPrefix}/${clinicId}/image-tool-exports`,
    {
      method: "POST",
      body: JSON.stringify({ images }),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      await getResponseError(
        response,
        "Unable to start the high-quality export",
      ),
    );
  }

  return response.json();
};

export const waitForCensorshipJob = ({
  metadataUrl,
  signal,
  intervalMs = 2000,
  timeoutMs = JOB_POLL_TIMEOUT_MS,
}: {
  metadataUrl: string;
  signal?: AbortSignal;
  intervalMs?: number;
  timeoutMs?: number;
}): Promise<TCensorshipJobMetadata> =>
  waitForJobMetadata({
    metadataUrl,
    signal,
    intervalMs,
    timeoutMs,
    statusLabel: "Processing",
  });

export const waitForImageToolExport = ({
  metadataUrl,
  signal,
  intervalMs = 2000,
  timeoutMs = JOB_POLL_TIMEOUT_MS,
}: {
  metadataUrl: string;
  signal?: AbortSignal;
  intervalMs?: number;
  timeoutMs?: number;
}): Promise<TImageToolExportMetadata> =>
  waitForJobMetadata({
    metadataUrl,
    signal,
    intervalMs,
    timeoutMs,
    statusLabel: "Export",
  });

export const downloadImageToolResult = async (
  url: string,
  filename: string,
) => {
  const response = await window.fetch(url);
  if (!response.ok) {
    throw new Error("This image could not be downloaded. Please try again.");
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};
